import React, { useState, useEffect } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';

// Helper function to map card string to image path
const getCardImage = (cardCode) => {
  if (!cardCode) return null;
  
  try {
    if (cardCode.length < 3) {
      return null;
    }
    
    // Use cards folder with full card code (e.g., "2DD.png", "ASS.png")
    // Format: rank + suit (DD, SS, CC, HH)
    return `/assets/img/cards/${cardCode}.png`;
  } catch (e) {
    return null;
  }
};

// Helper function to parse rdesc for aaa: "Amar#Even#Red#Under 7#2"
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};
  
  const parts = rdesc.split('#');
  const winner = parts[0] || "";
  const oddEven = parts[1] || "";
  const color = parts[2] || "";
  const underOver = parts[3] || "";
  const cardValue = parts[4] || "";
  
  return {
    winner,
    oddEven,
    color,
    underOver,
    cardValue,
  };
};

const ResultAaa = ({ 
  data = [], 
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('aaa');
        if (response?.success && response?.data?.data?.res) {
          // Map win values: "1" = "A" (Amar), "2" = "B" (Akbar), "3" = "C" (Anthony)
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win === "3" ? "C" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    
    fetchLastResults();
    const interval = setInterval(fetchLastResults, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleResultClick = async (resultItem, index) => {
    setSelectedResult(resultItem);
    setLoading(true);
    
    const mid = resultItem?.mid;
    
    if (mid) {
      try {
        const response = await getDetailResults('aaa', mid.toString());
        if (response?.success && response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }
    
    setLoading(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data based on selected result
  const getModalData = () => {
    if (!selectedResult || !detailResult) return null;

    const cardImage = getCardImage(detailResult.card);
    const rdescData = parseRdesc(detailResult.rdesc);
    const winner = detailResult.winnat || rdescData.winner || "";
    const winValue = detailResult.win;
    
    // Map win value to player name
    let winnerName = winner;
    if (winValue === "1") winnerName = "Amar";
    else if (winValue === "2") winnerName = "Akbar";
    else if (winValue === "3") winnerName = "Anthony";

    return {
      roundId: detailResult.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
      matchTime: detailResult.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      card: cardImage,
      resultDetails: {
        winner: winnerName,
        oddEven: rdescData.oddEven || "",
        color: rdescData.color || "",
        underOver: rdescData.underOver || "",
        card: rdescData.cardValue || "",
      },
    };
  };

  const modalData = getModalData();

  // Combine API results with fallback data
  const allResults = lastResults.length > 0 
    ? lastResults
    : data.map((res, idx) => ({ 
        win: res === "A" ? "A" : res === "B" ? "B" : res === "C" ? "C" : res, 
        mid: null 
      }));

  return (
    <>
      <div className="result-row dragon-tiger casino-last-results">
        {allResults.map((resultItem, index) => {
          const win = resultItem.win;
          // Map win to class: A = Amar (green/blue), B = Akbar (red), C = Anthony (green)
          let resultClass = "result-dragon"; // default blue/green for Amar
          if (win === "B") resultClass = "result-tiger"; // red for Akbar
          else if (win === "C") resultClass = "result-dragon"; // green for Anthony (same as Amar)
          
          return (
            <div
              key={index}
              className={`result-circle ${resultClass}`}
              onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
              style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
            >
              {win}
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Amar Akbar Anthony Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div className="row">
            <div className="col-12">
              <h6 className="text-right">
                <b>Round Id :</b> {modalData.roundId}
              </h6>
              <div className="text-center luckyseven-img">
                {modalData.card && (
                  <img src={modalData.card} alt="Card" />
                )}
              </div>
              <div className="col-md-12 text-left">
                {modalData.resultDetails.winner && (
                  <p className="m-b-0 m-t-20">
                    <b>Winner:</b> {modalData.resultDetails.winner}
                  </p>
                )}
                {modalData.resultDetails.oddEven && (
                  <p className="m-b-0">
                    <b>Odd/Even:</b> {modalData.resultDetails.oddEven}
                  </p>
                )}
                {modalData.resultDetails.color && (
                  <p className="m-b-0">
                    <b>Color:</b> {modalData.resultDetails.color}
                  </p>
                )}
                {modalData.resultDetails.underOver && (
                  <p className="m-b-0">
                    <b>Under/Over:</b> {modalData.resultDetails.underOver}
                  </p>
                )}
                {modalData.resultDetails.card && (
                  <p className="m-b-0">
                    <b>Card:</b> {modalData.resultDetails.card}
                  </p>
                )}
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultAaa;

